import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Search, Instagram, Youtube } from "lucide-react";
import { Reveal, Overline } from "../components/Motion";
import { Seo } from "../components/Seo";
import { ARTICLES, CONTENT, CATEGORIES } from "../lib/data";

const TABS = ["All", "Bytes", "Articles"];

const HEADINGS = {
  All: { overline: "Insights", title: "Everything, in one place.", sub: "Short bytes and long reads — markets, tax, IPOs and policy, explained clearly." },
  Bytes: { overline: "Bytes", title: "Learn something in under two minutes.", sub: "Quick, clear explainers from our Instagram and YouTube." },
  Articles: { overline: "Articles", title: "Long reads, worth your time.", sub: "Evidence-based deep dives with the reasoning laid out in full." },
};

const PlatformIcon = ({ platform }) => (platform === "youtube" ? <Youtube size={16} /> : <Instagram size={16} />);

const ByteCard = ({ c }) => (
  <motion.article
    layout
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`byte-card-${c.id}`}
    className="group"
  >
    <div className="overflow-hidden clip-frame h-52 relative">
      <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <div className="absolute top-4 left-4 h-9 w-9 rounded-full bg-cream/90 backdrop-blur flex items-center justify-center text-navy">
        <PlatformIcon platform={c.platform} />
      </div>
    </div>
    <div className="mt-5">
      <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-forest">
        <span>{c.category}</span><span className="text-navy/30">/</span><span className="text-navy/40">{c.date}</span>
      </div>
      <h3 className="font-heading text-2xl mt-3 tracking-tight group-hover:text-forest transition-colors">{c.title}</h3>
      <p className="text-navy/60 mt-2 leading-relaxed">{c.hook}</p>
    </div>
  </motion.article>
);

const ArticleCard = ({ a }) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
  >
    <Link to={`/insights/${a.slug}`} className="group block h-full" data-testid={`article-card-${a.slug}`}>
      <div className="overflow-hidden clip-frame h-52">
        <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <div className="mt-5">
        <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-forest">
          <span>{a.category}</span><span className="text-navy/30">/</span><span className="text-navy/40">{a.readTime}</span>
        </div>
        <h3 className="font-heading text-2xl mt-3 tracking-tight group-hover:text-forest transition-colors">{a.title}</h3>
        <p className="text-navy/60 mt-2 leading-relaxed">{a.excerpt}</p>
      </div>
    </Link>
  </motion.div>
);

export default function Insights() {
  const [tab, setTab] = useState("All");
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");

  const bytes = useMemo(() => {
    const q = query.toLowerCase();
    return CONTENT.filter((c) => {
      const mc = cat === "All" || c.category === cat;
      const mq = !q || c.title.toLowerCase().includes(q) || c.hook.toLowerCase().includes(q);
      return mc && mq;
    });
  }, [cat, query]);

  const articles = useMemo(() => {
    return ARTICLES.filter((a) => cat === "All" || a.category === cat);
  }, [cat]);

  const h = HEADINGS[tab];

  const switchTab = (t) => {
    setTab(t);
    setCat("All");
    setQuery("");
  };

  return (
    <div>
      <Seo />
      {/* Header */}
      <section className="pt-40 md:pt-52 pb-10 relative z-10" data-testid="insights-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Overline>{h.overline}</Overline>
          <AnimatePresence mode="wait">
            <motion.h1
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mt-6 font-medium max-w-4xl"
            >
              {h.title}
            </motion.h1>
          </AnimatePresence>
          <p className="text-navy/60 text-lg max-w-2xl mt-6">{h.sub}</p>
        </div>
      </section>

      {/* Tab bar */}
      <section className="pb-8 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 border-b border-navy/10 pb-6" data-testid="insights-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => switchTab(t)}
                data-testid={`tab-${t.toLowerCase()}`}
                className={`rounded-full px-6 py-2.5 text-sm font-medium tracking-wide transition-colors duration-300 ${
                  tab === t ? "bg-navy text-cream" : "text-navy/60 border border-navy/15 hover:border-navy"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Category filter + search (Bytes & Articles) */}
      {tab !== "All" && (
        <section className="pb-10 relative z-10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex flex-wrap gap-2" data-testid="insights-category-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`filter-${c.toLowerCase()}`}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${
                    cat === c ? "bg-forest text-cream" : "text-navy/60 border border-navy/15 hover:border-navy"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            {tab === "Bytes" && (
              <div className="flex items-center gap-3 border-b border-navy/20 focus-within:border-forest transition-colors lg:w-72">
                <Search size={18} className="text-navy/40" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search topics…"
                  data-testid="insights-search"
                  className="w-full bg-transparent outline-none py-2.5 text-navy placeholder:text-navy/40"
                />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Content */}
      <section className="pb-28 relative z-10" data-testid="insights-content">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* ALL — both sections */}
          {tab === "All" && (
            <div className="space-y-24">
              <div>
                <div className="flex items-end justify-between mb-10">
                  <h2 className="font-heading text-3xl md:text-4xl tracking-tight">Bytes <span className="text-navy/40 text-xl">· under two minutes</span></h2>
                  <button onClick={() => switchTab("Bytes")} className="text-navy font-medium hover:text-forest transition-colors flex items-center gap-2" data-testid="all-view-bytes">
                    View all <ArrowUpRight size={16} />
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {CONTENT.slice(0, 3).map((c) => <ByteCard key={c.id} c={c} />)}
                </div>
              </div>
              <div>
                <div className="flex items-end justify-between mb-10">
                  <h2 className="font-heading text-3xl md:text-4xl tracking-tight">Articles <span className="text-navy/40 text-xl">· long reads</span></h2>
                  <button onClick={() => switchTab("Articles")} className="text-navy font-medium hover:text-forest transition-colors flex items-center gap-2" data-testid="all-view-articles">
                    View all <ArrowUpRight size={16} />
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {ARTICLES.slice(0, 3).map((a) => <ArticleCard key={a.slug} a={a} />)}
                </div>
              </div>
            </div>
          )}

          {/* BYTES */}
          {tab === "Bytes" && (
            bytes.length === 0 ? (
              <p className="text-navy/50 text-lg py-20 text-center" data-testid="bytes-empty">No bytes match your search yet.</p>
            ) : (
              <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence mode="popLayout">
                  {bytes.map((c) => <ByteCard key={c.id} c={c} />)}
                </AnimatePresence>
              </motion.div>
            )
          )}

          {/* ARTICLES */}
          {tab === "Articles" && (
            articles.length === 0 ? (
              <p className="text-navy/50 text-lg py-20 text-center" data-testid="articles-empty">No articles in this category yet.</p>
            ) : (
              <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence mode="popLayout">
                  {articles.map((a) => <ArticleCard key={a.slug} a={a} />)}
                </AnimatePresence>
              </motion.div>
            )
          )}
        </div>
      </section>
    </div>
  );
}
